import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPosSyncToken } from '../api/posSyncApi';
import { getFormatOfferId } from '../utils/formatOffers';
import {
  sendPosSyncEvent,
  startPosSyncEvents,
  stopPosSyncEvents,
  subscribePosSyncEvents,
} from '../utils/posSyncEventsHub';

const EMPTY_SYNC_STATE = {
  activePurchaseId: null,
  updatedAt: null,
};
const normalizeDecimal = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeWeighing = (value) => {
  if (!value || typeof value !== 'object') return null;
  const formatOfferId = getFormatOfferId(value);
  if (value.formatOfferId != null && !formatOfferId) return null;
  return {
    activePurchaseId: normalizePurchaseId(value?.activePurchaseId),
    varietyId: normalizePurchaseId(value?.varietyId),
    varietyPriceId: normalizePurchaseId(value?.varietyPriceId),
    ...(formatOfferId ? { formatOfferId } : {}),
    productName: value?.productName ? String(value.productName).trim() : null,
    varietyName: value?.varietyName ? String(value.varietyName).trim() : null,
    metricUnit: value?.metricUnit ? String(value.metricUnit).trim() : null,
    unitPrice: normalizeDecimal(value?.unitPrice),
  };
};

const normalizePreview = (value) => {
  if (!value || typeof value !== 'object') return null;
  const purchaseItems = Array.isArray(value.purchaseItems)
    ? value.purchaseItems
    : Array.isArray(value.PurchaseItems)
      ? value.PurchaseItems
      : [];
  let createdAt = value.createdAt || null;
  if (createdAt) {
    const parsed = new Date(createdAt);
    createdAt = Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  return {
    ...value,
    id: normalizePurchaseId(value.id),
    createdAt,
    purchaseItems,
  };
};

const buildScopeKey = ({ branchId, cashRegisterId, cashRegisterSessionId, userId }) =>
  [branchId, cashRegisterId, cashRegisterSessionId, userId]
    .map((value) => String(value || '').trim())
    .join(':');

const normalizePurchaseId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const normalizeSyncState = (value) => ({
  activePurchaseId: normalizePurchaseId(value?.activePurchaseId),
  updatedAt: value?.updatedAt || null,
});

const usePosSyncSession = ({
  branchId,
  cashRegisterId,
  cashRegisterSessionId,
  userId,
  enabled,
  mode,
}) => {
  const [state, setState] = useState({
    enabled: false,
    connectionStatus: 'idle',
    hasHydrated: false,
    syncState: EMPTY_SYNC_STATE,
    weighing: null,
    preview: null,
    hasEditor: false,
    hasClient: false,
    lastPurchaseChanged: null,
    sessionClosed: null,
    lastError: null,
  });

  const scopeKey = useMemo(() => {
    if (!enabled) return '';
    return buildScopeKey({ branchId, cashRegisterId, cashRegisterSessionId, userId });
  }, [branchId, cashRegisterId, cashRegisterSessionId, enabled, userId]);

  const getConnectionConfig = useCallback(async () => {
    const response = await getPosSyncToken(branchId, cashRegisterId);
    if (
      cashRegisterSessionId
      && response?.cashRegisterSessionId
      && String(response.cashRegisterSessionId) !== String(cashRegisterSessionId)
    ) {
      throw new Error('La sesión de caja cambió antes de conectar la vista POS.');
    }
    return response;
  }, [branchId, cashRegisterId, cashRegisterSessionId]);

  useEffect(() => {
    if (!enabled || !scopeKey) {
      stopPosSyncEvents();
      setState({
        enabled: false,
        connectionStatus: 'idle',
        hasHydrated: false,
        syncState: EMPTY_SYNC_STATE,
        weighing: null,
        preview: null,
        hasEditor: false,
        hasClient: false,
        lastPurchaseChanged: null,
        sessionClosed: null,
        lastError: null,
      });
      return undefined;
    }

    setState((prev) => ({
      ...prev,
        enabled: true,
        connectionStatus: prev.connectionStatus === 'connected' ? 'connected' : 'connecting',
        lastError: null,
    }));
    startPosSyncEvents({ scopeKey, getConnectionConfig });
    return () => {
      stopPosSyncEvents();
    };
  }, [enabled, getConnectionConfig, scopeKey]);

  useEffect(() => subscribePosSyncEvents((event) => {
    if (!event?.type) return;

    if (event.type === 'pos.sync.connection') {
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        connectionStatus: event.connectionStatus || 'idle',
        hasHydrated: event.connectionStatus === 'idle' ? false : prev.hasHydrated,
        lastError: event.errorMessage || (event.connectionStatus === 'error' ? prev.lastError : null),
      }));
      return;
    }

    if (event.type === 'pos.sync.bootstrap') {
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        hasHydrated: true,
        syncState: normalizeSyncState(event.syncState),
        weighing: normalizeWeighing(event.weighing),
        preview: normalizePreview(event.preview),
        hasEditor: Boolean(event.hasEditor),
        hasClient: Boolean(event.hasClient),
        lastError: null,
      }));
      return;
    }

    if (event.type === 'pos.sync.state') {
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        hasHydrated: true,
        syncState: normalizeSyncState(event.syncState),
        weighing: typeof event.weighing === 'undefined' ? prev.weighing : normalizeWeighing(event.weighing),
        preview: typeof event.preview === 'undefined' ? prev.preview : normalizePreview(event.preview),
        hasEditor: typeof event.hasEditor === 'boolean' ? event.hasEditor : prev.hasEditor,
        hasClient: typeof event.hasClient === 'boolean' ? event.hasClient : prev.hasClient,
      }));
      return;
    }

    if (event.type === 'pos.sync.purchase_changed') {
      setState((prev) => ({
        ...prev,
        enabled: enabled && !!scopeKey,
        lastPurchaseChanged: {
          purchaseId: normalizePurchaseId(event.purchaseId),
          reason: event.reason || 'updated',
          updatedAt: event.updatedAt || new Date().toISOString(),
        },
      }));
      return;
    }

    if (event.type === 'pos.sync.session_closed') {
      if (event.cashRegisterSessionId
        && String(event.cashRegisterSessionId) !== String(cashRegisterSessionId)) {
        return;
      }
      setState((prev) => ({
        ...prev,
        sessionClosed: {
          branchId: normalizePurchaseId(event.branchId),
          cashRegisterId: normalizePurchaseId(event.cashRegisterId),
          cashRegisterSessionId: normalizePurchaseId(event.cashRegisterSessionId),
          reason: event.reason || 'session_closed',
          closedBy: normalizePurchaseId(event.closedBy),
          closedAt: event.closedAt || new Date().toISOString(),
        },
      }));
      return;
    }

  }), [cashRegisterSessionId, enabled, scopeKey]);

  useEffect(() => {
    if (!enabled || !scopeKey || state.connectionStatus !== 'connected' || !mode) return;
    sendPosSyncEvent({
      type: 'pos.sync.presence',
      mode,
    });
  }, [enabled, mode, scopeKey, state.connectionStatus]);

  const setActivePurchaseId = useCallback((activePurchaseId) => sendPosSyncEvent({
    type: 'pos.sync.set_active_purchase',
    activePurchaseId: normalizePurchaseId(activePurchaseId),
  }), []);

  const setPreview = useCallback((preview) => sendPosSyncEvent({
    type: 'pos.sync.set_preview',
    preview: normalizePreview(preview),
  }), []);

  const setWeighing = useCallback((weighing) => sendPosSyncEvent({
    type: 'pos.sync.set_weighing',
    weighing: normalizeWeighing(weighing),
  }), []);

  return {
    ...state,
    activePurchaseId: state.syncState.activePurchaseId,
    setActivePurchaseId,
    setPreview,
    setWeighing,
  };
};

export default usePosSyncSession;
